// Reusable drillable insights panel — mirrors MTM Summary Dashboard look & feel.
// Drives off the generic /v1/projects/{pid}/modules/{moduleKey}/insights endpoint
// and /v1/projects/{pid}/modules/{moduleKey}/drilldowns/{kind} endpoint.
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import {
  Loader2, AlertOctagon, AlertTriangle, CheckCircle2, Activity, Compass, TrendingUp,
  ArrowRight, RefreshCw, X, ChevronRight, FlaskConical, BookOpen,
} from 'lucide-react';
import ModuleHelp from '@/components/common/ModuleHelp';

const SEVERITY_COLOR = {
  CRITICAL: { bg: '#ef4444', tone: '#ef444415', border: '#ef444440', text: '#ef4444', Icon: AlertOctagon },
  HIGH:     { bg: '#f97316', tone: '#f9731615', border: '#f9731640', text: '#f97316', Icon: AlertTriangle },
  MEDIUM:   { bg: '#f59e0b', tone: '#f59e0b15', border: '#f59e0b40', text: '#f59e0b', Icon: AlertTriangle },
  LOW:      { bg: '#3b82f6', tone: '#3b82f615', border: '#3b82f640', text: '#3b82f6', Icon: AlertTriangle },
};

export default function ModuleInsightsDashboard({
  projectId, moduleKey, color = '#143A6B', deepLinkBase = '',
  showHelp = true, embedded = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // {kind, title}

  const load = async () => {
    if (!projectId || !moduleKey) return;
    setLoading(true);
    try {
      const r = await api.get(`/v1/projects/${projectId}/modules/${moduleKey}/insights`);
      setData(r.data);
    } catch { setData(null); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, moduleKey]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;
  if (!data) return (
    <div className="p-4 text-sm rounded-lg" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-muted)' }} data-testid={`module-insights-empty-${moduleKey}`}>
      No insights available yet for this module. Once activity flows in, risks, actions and qualitative insights will appear here automatically.
    </div>
  );

  const h = data.headline || {};
  const stats = data.stats || {};

  return (
    <div className={embedded ? 'space-y-3' : 'space-y-4'} data-testid={`module-insights-${moduleKey}`}>
      {/* Header strip */}
      <div className="flex items-center justify-between flex-wrap gap-2 pb-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
          Live snapshot · {data.evaluatedAt ? new Date(data.evaluatedAt).toLocaleString() : '—'}
        </span>
        <div className="flex items-center gap-2">
          {showHelp && <ModuleHelp moduleKey={moduleKey} compact />}
          <button onClick={load} data-testid={`module-insights-refresh-${moduleKey}`}
            className="flex items-center gap-1 px-3 py-1.5 text-[11px] rounded font-semibold"
            style={{ background: 'var(--card-bg)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Headline KPIs */}
      {h.kpis && h.kpis.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {h.kpis.map((k, i) => (
            <KPI key={i} label={k.label} value={k.value} color={k.color || color} />
          ))}
        </div>
      )}

      {/* Risks / Actions / Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <RAIColumn title="Risks" Icon={AlertOctagon} color="#ef4444"
          testIdPrefix={`module-${moduleKey}-risk`}
          items={data.risks || []}
          onDrill={(it) => setDrill({ kind: it.drillKind, title: it.title })}
          emptyTitle="No active risks" emptyBody="This module is healthy." />
        <RAIColumn title="Recommended Actions" Icon={Compass} color="#3b82f6"
          testIdPrefix={`module-${moduleKey}-action`}
          items={data.actions || []}
          onDrill={(it) => setDrill({ kind: it.drillKind, title: it.title })}
          emptyTitle="All caught up" emptyBody="No pending actions surfaced by the auto-pilot." />
        <RAIColumn title="Qualitative Insights" Icon={TrendingUp} color={color}
          testIdPrefix={`module-${moduleKey}-insight`}
          items={data.insights || []}
          onDrill={(it) => setDrill({ kind: it.drillKind, title: it.title })}
          emptyTitle="No data yet" emptyBody="Once activity flows in, narrative insights will appear here." />
      </div>

      {/* Stat ribbon */}
      {Object.keys(stats).length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-11 gap-1 text-[10px]">
          {Object.entries(stats).map(([key, val]) => (
            <Stat key={key} label={prettifyKey(key)} value={typeof val === 'object' ? val.value : val}
              accent={typeof val === 'object' ? val.accent : '#64748b'} />
          ))}
        </div>
      )}

      {drill && (
        <DrillModal projectId={projectId} moduleKey={moduleKey}
          kind={drill.kind} title={drill.title}
          deepLinkBase={deepLinkBase} color={color}
          onClose={() => setDrill(null)} />
      )}
    </div>
  );
}

function RAIColumn({ title, Icon, color, items, onDrill, testIdPrefix, emptyTitle, emptyBody }) {
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-column`}>
      <h3 className="text-[12px] uppercase tracking-wide font-bold flex items-center gap-1" style={{ color }}>
        <Icon className="w-3.5 h-3.5" /> {title} ({items.length})
      </h3>
      {items.length === 0 && (
        <div className="p-3 rounded-lg text-center" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <CheckCircle2 className="w-5 h-5 mx-auto mb-1" style={{ color: '#22c55e' }} />
          <div className="text-[11px] font-semibold" style={{ color: '#22c55e' }}>{emptyTitle}</div>
          <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{emptyBody}</p>
        </div>
      )}
      {items.map((it, i) => {
        const sev = it.severity ? SEVERITY_COLOR[it.severity] : null;
        const tone = sev?.tone || `${color}15`;
        const border = sev?.border || `${color}40`;
        const textColor = sev?.text || color;
        const clickable = !!it.drillKind;
        const ItemIcon = sev?.Icon || Activity;
        return (
          <button key={i} disabled={!clickable}
            data-testid={`${testIdPrefix}-${i}`}
            onClick={() => clickable && onDrill(it)}
            className="w-full text-left p-3 rounded-lg disabled:cursor-default"
            style={{ background: tone, border: `1px solid ${border}`, cursor: clickable ? 'pointer' : 'default' }}>
            <div className="flex items-start gap-2">
              <ItemIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: textColor }} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  {sev && <span className="text-[8px] font-bold px-1 py-0 rounded text-white" style={{ background: sev.bg }}>{it.severity}</span>}
                  <span className="text-[12px] font-semibold" style={{ color: textColor }}>{it.title}</span>
                  {clickable && <ChevronRight className="w-3 h-3 ml-auto" style={{ color: textColor }} />}
                </div>
                {it.detail && (
                  it.narrative
                    ? <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}
                        dangerouslySetInnerHTML={{ __html: it.detail.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                    : <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{it.detail}</p>
                )}
                {it.narrative && !it.detail && (
                  <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}
                    dangerouslySetInnerHTML={{ __html: it.narrative.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                )}
                {it.action && (
                  <div className="mt-1.5 text-[10px] font-medium flex items-center gap-1" style={{ color: textColor }}>
                    <ArrowRight className="w-2.5 h-2.5" /> {it.action}
                  </div>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function DrillModal({ projectId, moduleKey, kind, title, onClose, deepLinkBase, color }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!projectId || !kind) return;
    api.get(`/v1/projects/${projectId}/modules/${moduleKey}/drilldowns/${kind}`, { params: { limit: 200 } })
      .then(r => setData(r.data))
      .catch(() => setData({ items: [], count: 0, title: 'Failed to load' }));
  }, [projectId, moduleKey, kind]);

  const columns = guessColumns(data?.items?.[0] || {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.65)' }} onClick={onClose} data-testid={`module-drill-modal-${moduleKey}`}>
      <div onClick={e => e.stopPropagation()} className="max-w-5xl w-full rounded-lg max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        <div className="sticky top-0 px-4 py-3 flex items-center justify-between" style={{ background: color, color: '#fff', zIndex: 10 }}>
          <div>
            <div className="text-[10px] uppercase tracking-wider opacity-80">Drilldown</div>
            <div className="text-base font-bold">{data?.title || title}</div>
            {data && <div className="text-[10px] opacity-80">{data.count} record(s)</div>}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/20" data-testid={`module-drill-close-${moduleKey}`}><X className="w-4 h-4" /></button>
        </div>
        {!data ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
        ) : (
          <div className="p-4">
            {data.items.length === 0 ? (
              <div className="text-center py-8 text-[12px]" style={{ color: 'var(--text-muted)' }}>
                <CheckCircle2 className="w-6 h-6 mx-auto mb-2" style={{ color: '#22c55e' }} />
                Nothing here — this category is currently empty.
              </div>
            ) : (
              <div className="rounded-lg overflow-x-auto" style={{ border: '1px solid var(--border-color)' }} data-testid={`module-drill-table-${moduleKey}`}>
                <table className="w-full text-[11px]">
                  <thead><tr style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
                    {columns.map(c => <th key={c.key} className="text-left px-2 py-2 font-medium whitespace-nowrap">{c.label}</th>)}
                    <th className="text-left px-2 py-2 font-medium"></th>
                  </tr></thead>
                  <tbody>
                    {data.items.map((it, i) => (
                      <tr key={i} data-testid={`module-drill-row-${moduleKey}-${i}`} style={{ borderTop: '1px solid var(--border-color)' }}>
                        {columns.map(c => (
                          <td key={c.key} className="px-2 py-1.5 whitespace-nowrap" style={c.style || {}}>
                            {c.render ? c.render(it[c.key], it) : (it[c.key] ?? '—')}
                          </td>
                        ))}
                        <td className="px-2 py-1.5">
                          {it.deepLink && (
                            <a href={`${deepLinkBase || ''}${it.deepLink}`}
                              data-testid={`module-drill-open-${moduleKey}-${i}`}
                              className="text-[10px] font-semibold inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded"
                              style={{ background: color, color: '#fff' }}>
                              Open <ArrowRight className="w-2.5 h-2.5" />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function guessColumns(sample) {
  const preferred = [
    { key: 'code',          label: 'Code',         style: { fontFamily: 'monospace', fontWeight: 600 } },
    { key: 'id',            label: 'ID',           style: { fontFamily: 'monospace' } },
    { key: 'number',        label: 'No.',          style: { fontFamily: 'monospace', fontWeight: 600 } },
    { key: 'materialCode',  label: 'Material',     style: { fontFamily: 'monospace', fontWeight: 600 } },
    { key: 'spoolNumber',   label: 'Spool',        style: { fontFamily: 'monospace', fontWeight: 600 } },
    { key: 'markNumber',    label: 'Mark',         style: { fontFamily: 'monospace', fontWeight: 600 } },
    { key: 'lineNumber',    label: 'Line',         style: { fontFamily: 'monospace', fontWeight: 600 } },
    { key: 'tagNumber',     label: 'Tag',          style: { fontFamily: 'monospace', fontWeight: 600 } },
    { key: 'package',       label: 'Package' },
    { key: 'packageName',   label: 'Package' },
    { key: 'poNumber',      label: 'PO',           style: { fontFamily: 'monospace' } },
    { key: 'prNumber',      label: 'PR',           style: { fontFamily: 'monospace' } },
    { key: 'vendorName',    label: 'Vendor' },
    { key: 'type',          label: 'Type' },
    { key: 'category',      label: 'Category' },
    { key: 'status',        label: 'Status' },
    { key: 'severity',      label: 'Severity' },
    { key: 'priority',      label: 'Priority' },
    { key: 'area',          label: 'Area' },
    { key: 'discipline',    label: 'Discipline' },
    { key: 'title',         label: 'Title' },
    { key: 'description',   label: 'Description' },
    { key: 'owner',         label: 'Owner' },
    { key: 'value',         label: 'Value',        style: { fontFamily: 'monospace' } },
    { key: 'amount',        label: 'Amount',       style: { fontFamily: 'monospace' } },
    { key: 'quantity',      label: 'Qty',          style: { fontFamily: 'monospace' } },
    { key: 'shortfall',     label: 'Shortfall',    style: { fontFamily: 'monospace', color: '#ef4444' } },
    { key: 'percentComplete', label: '%',          render: v => v != null ? `${Math.round(v)}%` : '—' },
    { key: 'daysSlip',      label: 'Slip (d)',     style: { fontFamily: 'monospace', color: '#ef4444' } },
    { key: 'rosDate',       label: 'ROS',          render: v => v ? String(v).slice(0, 10) : '—' },
    { key: 'dueDate',       label: 'Due',          render: v => v ? String(v).slice(0, 10) : '—' },
    { key: 'plannedDate',   label: 'Planned',      render: v => v ? String(v).slice(0, 10) : '—' },
    { key: 'actualDate',    label: 'Actual',       render: v => v ? String(v).slice(0, 10) : '—' },
    { key: 'createdAt',     label: 'Created',      render: v => v ? String(v).slice(0, 10) : '—' },
  ];
  return preferred.filter(c => c.key in sample);
}

function KPI({ label, value, color }) {
  return (
    <div className="p-3 rounded-lg" style={{ background: 'var(--card-bg)', border: `1px solid ${color}30` }}>
      <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="text-xl font-bold font-mono" style={{ color }}>{value || '—'}</div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="p-2 rounded text-center" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="text-[8px] uppercase tracking-wide truncate" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="text-base font-bold font-mono" style={{ color: accent }}>{value ?? '—'}</div>
    </div>
  );
}

function prettifyKey(k) {
  return k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim();
}
